// src/components/lesson/WorkedExampleCard.tsx
//
// Worked-example card for the lesson page. Two render modes:
//
//   1. Interactive (v2): the example carries `choices[]` (4 entries,
//      A/B/C/D) AND `correctChoice`. Renders the stem, the four
//      clickable choices, and on click locks the selection, shows
//      correct/incorrect feedback, then reveals the worked solution +
//      insight. A "Try another" button lets the student retry.
//
//   2. Reveal-only (legacy / SPR): no choices on the example. Shows the
//      old "Try it first — show the solution" button that uncovers the
//      solution + insight. Used for student-produced-response (numeric
//      input) examples and any pre-v2 lesson that hasn't been backfilled.
//
// Markdown rendering is injected via the `renderMarkdown` prop so tests
// can substitute a plain renderer instead of pulling in ReactMarkdown +
// KaTeX + remark plugins. The lesson page passes its
// `MarkdownWithDiagrams` helper.

'use client'

import AskSageButton from '@/components/tutor/AskSageButton'
import type { AskSageRequest } from '@/lib/tutor/on-screen-material'
import { useState, type ReactNode } from 'react'
import { AlertTriangle, CheckCircle2, Lightbulb } from 'lucide-react'
import type { WorkedExampleChoice } from '@/lib/study-plan/lesson-generation'
import { normalizeWorkedExample } from '@/lib/learn/worked-example-choices'

export type WorkedExampleData = {
  problem: string
  solution: string
  insight: string
  choices?: WorkedExampleChoice[]
  correctChoice?: 'A' | 'B' | 'C' | 'D'
}

export default function WorkedExampleCard({
  index,
  example: stored,
  renderMarkdown,
  ask,
}: {
  index: number
  example: WorkedExampleData
  renderMarkdown: (content: string) => ReactNode
  /** "Ask Sage about this example" (hides itself when Sage can't open here). */
  ask?: AskSageRequest
}) {
  // Older lessons wrote the choices inline in the problem ("**A)** `,` …");
  // show those as real choices too (worked-example-choices.ts).
  const example = normalizeWorkedExample(stored)
  const interactive =
    Array.isArray(example.choices) && example.choices.length === 4 && !!example.correctChoice

  const [picked, setPicked] = useState<'A' | 'B' | 'C' | 'D' | null>(null)
  const [revealed, setRevealed] = useState(false)

  const showSolution = interactive ? picked !== null : revealed

  return (
    <div className="rounded-lg border border-border/60 bg-card/40 p-4">
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="text-xs uppercase tracking-wide text-brand-ink/70">Example {index + 1}</div>
        {ask && <AskSageButton request={ask} />}
      </div>
      <div className="prose dark:prose-invert max-w-none prose-strong:text-foreground prose-code:rounded prose-code:bg-slate-100 dark:prose-code:bg-background prose-code:px-1 prose-code:py-0.5 prose-code:text-brand-ink dark:prose-code:text-brand-ink prose-code:before:content-none prose-code:after:content-none">
        {renderMarkdown(example.problem)}
      </div>

      {interactive && example.choices && example.correctChoice && (
        <ChoiceList
          choices={example.choices}
          correct={example.correctChoice}
          picked={picked}
          onPick={setPicked}
        />
      )}

      {!showSolution && !interactive && (
        <button
          type="button"
          onClick={() => setRevealed(true)}
          className="mt-3 inline-flex items-center gap-1 rounded-md border border-brand/40 bg-brand/10 px-3 py-1.5 text-sm text-brand-ink transition-colors hover:bg-brand/20"
        >
          Try it first — show the solution
        </button>
      )}

      {showSolution && (
        <>
          {interactive && picked && example.correctChoice && (
            <div
              role="status"
              className={`mt-3 flex items-center gap-2 rounded-md px-3 py-2 text-sm ${
                picked === example.correctChoice
                  ? 'border border-emerald-500/30 bg-emerald-50 text-emerald-800 dark:bg-emerald-950/20 dark:text-emerald-200'
                  : 'border border-rose-500/30 bg-rose-50 text-rose-800 dark:bg-rose-950/20 dark:text-rose-200'
              }`}
            >
              {picked === example.correctChoice ? (
                <>
                  <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
                  <span>
                    Correct! <strong>{picked}</strong> is the answer.
                  </span>
                </>
              ) : (
                <>
                  <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                  <span>
                    Not quite. You picked <strong>{picked}</strong>; the answer is{' '}
                    <strong>{example.correctChoice}</strong>. See the solution below.
                  </span>
                </>
              )}
            </div>
          )}

          <div className="mt-3 rounded-md bg-background/70 p-3">
            <div className="mb-1 text-xs uppercase tracking-wide text-muted-foreground">
              Solution
            </div>
            <div className="prose dark:prose-invert max-w-none text-sm prose-strong:text-foreground prose-code:rounded prose-code:bg-slate-100 dark:prose-code:bg-card prose-code:px-1 prose-code:py-0.5 prose-code:text-brand-ink dark:prose-code:text-brand-ink prose-code:before:content-none prose-code:after:content-none">
              {renderMarkdown(example.solution)}
            </div>
          </div>

          {example.insight && (
            <div className="mt-3 flex items-start gap-2 rounded-md border border-brand/20 bg-brand/10 dark:bg-brand/20 p-3 text-sm text-brand-ink">
              <Lightbulb className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <span>{example.insight}</span>
            </div>
          )}

          {interactive && (
            <button
              type="button"
              onClick={() => setPicked(null)}
              className="mt-3 text-xs text-brand-ink underline-offset-2 hover:underline dark:text-brand-ink"
            >
              Try another answer
            </button>
          )}
        </>
      )}
    </div>
  )
}

/** Four-choice clickable list. Once a choice is picked, all four lock and
 *  the correct one stays highlighted in green; an incorrect pick is
 *  highlighted in rose. Keyboard-friendly via real <button>s with
 *  role="radio". */
function ChoiceList({
  choices,
  correct,
  picked,
  onPick,
}: {
  choices: WorkedExampleChoice[]
  correct: 'A' | 'B' | 'C' | 'D'
  picked: 'A' | 'B' | 'C' | 'D' | null
  onPick: (l: 'A' | 'B' | 'C' | 'D') => void
}) {
  const locked = picked !== null
  return (
    <div role="radiogroup" aria-label="Answer choices" className="mt-3 grid gap-2">
      {choices.map((c) => {
        const isPicked = picked === c.label
        const isCorrect = c.label === correct
        const showCorrectness = locked && (isPicked || isCorrect)
        let stateClass = 'border-border/60 bg-card hover:bg-muted hover:border-brand/40'
        if (showCorrectness) {
          if (isCorrect) {
            stateClass =
              'border-emerald-500/50 bg-emerald-50 text-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-100'
          } else if (isPicked) {
            stateClass =
              'border-rose-500/50 bg-rose-50 text-rose-900 dark:bg-rose-950/30 dark:text-rose-100'
          }
        } else if (locked) {
          stateClass = 'border-border/40 bg-card opacity-70'
        }
        return (
          <button
            key={c.label}
            type="button"
            role="radio"
            aria-checked={isPicked}
            disabled={locked}
            onClick={() => !locked && onPick(c.label)}
            className={`flex items-start gap-3 rounded-md border px-3 py-2 text-left text-sm transition-colors ${stateClass} ${
              locked ? 'cursor-default' : 'cursor-pointer'
            }`}
          >
            <span className="mt-0.5 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border border-current text-xs font-semibold">
              {c.label}
            </span>
            <span className="flex-1">{c.text}</span>
          </button>
        )
      })}
    </div>
  )
}
